const DANGEROUS_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

function scan(value, depth = 0) {
  if (depth > 80) throw new Error('Project structure is too deeply nested.');
  if (!value || typeof value !== 'object') return;
  for (const key of Object.keys(value)) {
    if (DANGEROUS_KEYS.has(key)) throw new Error(`Unsafe key: ${key}`);
    scan(value[key], depth + 1);
  }
}

export function validateProject(project) {
  scan(project);
  const errors = [];
  if (!project || typeof project !== 'object') errors.push('Project must be an object.');
  if (project?.schemaVersion !== 1) errors.push('Unsupported schemaVersion.');
  for (const key of ['id', 'name', 'categoryId', 'templateId', 'floors', 'objects', 'materials']) {
    if (project?.[key] === undefined) errors.push(`Missing ${key}.`);
  }
  if (!Array.isArray(project?.floors) || project.floors.length < 1 || project.floors.length > 12) errors.push('Project requires 1–12 floors.');
  if (!Array.isArray(project?.objects) || !Array.isArray(project?.materials)) errors.push('Invalid project collections.');
  for (const floor of project?.floors ?? []) {
    if (!floor || typeof floor !== 'object' || !floor.id) {
      errors.push('Every floor requires an id.');
      continue;
    }
    if (floor.baseY !== undefined && (!Number.isFinite(floor.baseY) || floor.baseY < -20 || floor.baseY > 100)) {
      errors.push(`Floor ${floor.id} has invalid baseY.`);
    }
    for (const key of ['rooms', 'walls', 'doors', 'windows', 'stairs', 'objects']) {
      if (!Array.isArray(floor[key])) errors.push(`Floor ${floor.id} has invalid ${key}.`);
    }
    if (floor.measurements !== undefined && !Array.isArray(floor.measurements)) errors.push(`Floor ${floor.id} has invalid measurements.`);
    const limits = { rooms: 500, walls: 2000, doors: 2000, windows: 4000, stairs: 500, objects: 5000, measurements: 2000 };
    for (const [key, limit] of Object.entries(limits)) if ((floor[key]?.length ?? 0) > limit) errors.push(`Floor ${floor.id} exceeds the safe ${key} limit.`);
    for (const wall of floor.walls ?? []) {
      const points = [...(wall.start ?? []), ...(wall.end ?? [])];
      if (points.length !== 4 || points.some((value) => typeof value !== 'number' || !Number.isFinite(value))) errors.push(`Wall ${wall.id ?? 'unknown'} has invalid coordinates.`);
      if (typeof wall.height !== 'number' || !Number.isFinite(wall.height) || wall.height < 0.2 || wall.height > 20) errors.push(`Wall ${wall.id ?? 'unknown'} has invalid height.`);
    }
    for (const measurement of floor.measurements ?? []) {
      const points = [...(measurement.start ?? []), ...(measurement.end ?? [])];
      if (points.length !== 4 || points.some((value) => typeof value !== 'number' || !Number.isFinite(value))) errors.push(`Measurement ${measurement.id ?? 'unknown'} is invalid.`);
    }
  }
  if ((project?.objects?.length ?? 0) > 5000 || (project?.materials?.length ?? 0) > 1000) errors.push('Project exceeds safe root collection limits.');
  if (errors.length) throw new Error(errors.join(' '));
  const clone = structuredClone(project);
  for (const floor of clone.floors) floor.measurements ??= [];
  return clone;
}
